import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { Currency } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { updateSubscriptionCurrency } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
  currencyDisplayMapping,
  currencyMapping,
} from "constants/index";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import {
  ChangeSubscriptionCurrencyWrapperProps,
  UrlParamTypes,
} from "types/org-display";

interface ChangeSubscriptionCurrencyProps
  extends ChangeSubscriptionCurrencyWrapperProps {
  provider?: string;
}

// Define currency arrays outside component to avoid recreation on each render
// Zuora subscriptions support all currencies including AUD
const ZUORA_ALLOWED_CURRENCIES = [
  Currency.USD,
  Currency.EUR,
  Currency.GBP,
  Currency.AUD,
];
// Chargify subscriptions don't allow transitions to/from AUD due to separate Chargify site
const CHARGIFY_ALLOWED_CURRENCIES = [Currency.USD, Currency.EUR, Currency.GBP];

const ChangeSubscriptionCurrency: React.FC<ChangeSubscriptionCurrencyProps> = ({
  refreshOrgData,
  provider,
}: ChangeSubscriptionCurrencyProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const isZuora = provider === "zuora";
  const allowableCurrencies = isZuora
    ? ZUORA_ALLOWED_CURRENCIES
    : CHARGIFY_ALLOWED_CURRENCIES;

  const subscription = orgData?.subscription?.[0];
  const normalisedSubData = subData?.subscription;

  // Get site only for Chargify subscriptions
  const site = !isZuora ? subscription?.chargify?.site || "" : "";

  // Get currency based on provider
  const currency = normalisedSubData?.currency?.toUpperCase();

  const [currentCurrency, setCurrentCurrency] = useState<Currency>(
    currencyMapping[currency as keyof typeof currencyMapping] ?? Currency.USD
  );

  const currentCurrencyCode = currencyDisplayMapping[currentCurrency];

  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>(
    allowableCurrencies.filter(
      (currency) => currency != currentCurrency
    ) as Currency[]
  );

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState<Currency>(
    currencyOptions[0]
  );

  // Get subscription ID based on provider
  const subscriptionId = normalisedSubData?.subscriptionId;

  const subscriptionState =
    normalisedSubData?.state || SubscriptionState.UNSPECIFIED;

  const isCurrencyUpdateStateValid = [
    SubscriptionState.ACTIVE,
    SubscriptionState.TRIALING,
  ].includes(subscriptionState);

  const isSubscriptionFree = normalisedSubData?.tier === Tier.FREE;

  const isActiveSubscription = normalisedSubData?.active;

  const isCurrencyUpdateBtnDisabled =
    isSubscriptionActionLoading ||
    isSubscriptionFree ||
    !isActiveSubscription ||
    (!isZuora && site !== "sc-usd-s") ||
    !isCurrencyUpdateStateValid;

  const handleSnackbarClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const changeTargetCurrency = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const num = parseInt(event.target.value as string, 10);
    setTargetCurrency(num);
  };

  const handleChangeSubscriptionCurrencyState = (updatedCurrency: Currency) => {
    if (!currency) return;
    setCurrentCurrency(updatedCurrency);
  };

  const handleChangeSubscriptionCurrency = (updatedCurrency: Currency) => {
    if (!subscriptionId) {
      showError({ message: "No subscription ID found" } as GrpcError);
      return;
    }
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    updateSubscriptionCurrency(
      {
        subscriptionId: subscriptionId,
        currency: currencyDisplayMapping[updatedCurrency].toLowerCase(),
        orgId: roleId,
      },
      callOptionsHeaders(regionCode, subscriptionId)
    )
      .then(() => {
        handleChangeSubscriptionCurrencyState(updatedCurrency);
        const updatedCurrencies = allowableCurrencies.filter(
          (currency) => currency != updatedCurrency
        ) as Currency[];
        setCurrencyOptions(updatedCurrencies);
        setTargetCurrency(updatedCurrencies[0]);
        setOpenSuccessAlert(true);
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title={"Currency:"}
        middleElem={
          <Typography
            className={clsx(classes.spaceTop, classes.tableTypography)}
          >
            {!isSubscriptionFree ? currentCurrencyCode : NO_DISPLAY_DATA}
          </Typography>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            disabled={isCurrencyUpdateBtnDisabled}
            fullWidth
            data-testid="btn-change-billing-currency"
            onClick={() => setOpenConfirmDialog(true)}
          >
            Change Currency
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Change Billing Currency"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleChangeSubscriptionCurrency(targetCurrency)}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        <Grid container>
          <Grid
            container
            justifyContent="flex-start"
            data-testid="modal-change-billing-currency"
          >
            <Typography
              variant="body1"
              data-test-id="change-subscription-currency-initial-currency"
            >
              Update subscription currency from <b>{currentCurrencyCode}</b> to:
            </Typography>
          </Grid>
          <RadioGroup onChange={changeTargetCurrency} value={targetCurrency}>
            <Grid container direction="column">
              {currencyOptions.map((option: Currency) => {
                const testId = `radio-change-billing-currency-${currencyDisplayMapping[
                  option
                ].toLowerCase()}`;
                return (
                  <FormControlLabel
                    key={option}
                    data-testid={testId}
                    value={option}
                    name="currency-options"
                    control={<Radio />}
                    label={currencyDisplayMapping[option]}
                  />
                );
              })}
            </Grid>
          </RadioGroup>
        </Grid>
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
      >
        <Alert severity="success" variant="filled">
          Currency updated
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default ChangeSubscriptionCurrency;
